import { useState, useRef } from "react";
import { getColorPair } from "../../../../helper/randowColor";
import Avvvatars from "avvvatars-react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface Props {
  days: any[];
  sessions: any[];
  courses: any;
}

const MINUTE_HEIGHT = 1.5; // 1 minute = 1.5px

const StudentEDTGrid = ({ days, sessions, courses }: Props) => {
  const [activeDayIndex, setActiveDayIndex] = useState<number>(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const gridTemplateRows = [
    "50px", // entête des jours
    ...sessions.map((s) => `${s.duration * MINUTE_HEIGHT}px`),
  ].join(" ");

  const occupiedCells = new Set<string>();
  Object.entries(courses).forEach(([dayName, sessionsObj]: any) => {
    Object.values(sessionsObj).forEach((courseList: unknown) => {
      (courseList as any[]).forEach((course) => {
        const dayIndex = days.findIndex((d) => d.name === dayName);
        if (dayIndex === -1) return;
        const startRow = sessions.findIndex(
          (s) => s.id === course.sessionStart.id,
        );
        const endRow = sessions.findIndex((s) => s.id === course.sessionEnd.id);
        if (startRow === -1 || endRow === -1) return;
        for (let row = startRow; row <= endRow; row++) {
          occupiedCells.add(`${dayIndex}-${row}`);
        }
      });
    });
  });

  // On mobile, display only the selected day
  const visibleDays = days;

  const renderGrid = (daysToRender: any[]) => (
    <div
      className="edt-grid"
      style={{
        gridTemplateColumns: `var(--edt-time-col-width, 80px) repeat(${daysToRender.length}, minmax(var(--edt-day-min-width, 220px), 1fr))`,
        gridTemplateRows,
      }}
    >
      {/* Coin vide haut gauche */}
      <div
        className="edt-day-header edt-day-header-empty"
        style={{ gridRow: 1, gridColumn: 1 }}
      />

      {/* Entêtes des jours */}
      {daysToRender.map((day, index) => (
        <div
          key={day.id}
          className="edt-day-header"
          style={{ gridRow: 1, gridColumn: index + 2 }}
        >
          {day.name}
        </div>
      ))}

      {/* Colonne des heures */}
      {sessions.map((session, index) => (
        <div
          key={session.id}
          className="edt-time-slot"
          style={{ gridRow: index + 2, gridColumn: 1 }}
        >
          <span className="edt-time-label">{session.start_time}</span>
          <span className="edt-time-label edt-time-end">
            {session.end_time}
          </span>
        </div>
      ))}

      {/* Cellules vides */}
      {daysToRender.map((day, dayIndex) =>
        sessions.map((session, sessionIndex) => {
          const globalDayIndex = days.findIndex((d) => d.id === day.id);
          const cellKey = `${globalDayIndex}-${sessionIndex}`;
          const isOccupied = occupiedCells.has(cellKey);
          return (
            <div
              key={`${dayIndex}-${sessionIndex}`}
              className="edt-grid-cell"
              style={{ gridColumn: dayIndex + 2, gridRow: sessionIndex + 2 }}
            >
              {!isOccupied && (
                <div className="edt-empty-course-card">Aucun cours</div>
              )}
            </div>
          );
        }),
      )}

      {/* Cours */}
      {Object.entries(courses).flatMap(([dayName, sessionsObj]: any) =>
        Object.values(sessionsObj as Record<string, any[]>).flatMap(
          (courseList: any[]) =>
            courseList.map((course) => {
              const globalDayIndex = days.findIndex((d) => d.name === dayName);
              const localDayIndex = daysToRender.findIndex(
                (d) => d.name === dayName,
              );
              if (localDayIndex === -1) return null;

              const startRow = sessions.findIndex(
                (s) => s.id === course.sessionStart.id,
              );
              const endRow = sessions.findIndex(
                (s) => s.id === course.sessionEnd.id,
              );
              if (startRow === -1 || endRow === -1) return null;

              const colors = getColorPair(course.teacherId ?? course.teacher ?? course.name);

              return (
                <div
                  key={`${globalDayIndex}-${course.id}`}
                  className="edt-course-card"
                  style={{
                    gridColumn: localDayIndex + 2,
                    gridRow: `${startRow + 2} / ${endRow + 3}`,
                    backgroundColor: colors.light,
                    border: `1px solid ${colors.dark}`,
                  }}
                >
                  {/* Heure */}
                  <div className="edt-course-time">
                    <i className="ti ti-clock" />
                    <span>
                      {course.sessionStart.start_time.slice(0, 5)} –{" "}
                      {course.sessionEnd.end_time.slice(0, 5)}
                    </span>
                  </div>

                  {/* Nom du cours */}
                  <div className="edt-course-body">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-${course.id}`}>
                          {course.name}
                        </Tooltip>
                      }
                    >
                      <div
                        className="edt-course-title"
                        style={{ color: colors.dark }}
                      >
                        {course.name}
                      </div>
                    </OverlayTrigger>
                  </div>

                  {/* Pied de carte */}
                  <div className="edt-course-footer">
                    <div className="edt-course-room">
                      <i className="ti ti-map-pin" />
                      <span>{course.room}</span>
                    </div>

                    <div className="edt-teacher">
                      <Avvvatars
                        value={course.teacher}
                        style="character"
                        size={30}
                      />
                      <span className="edt-teacher-name">{course.teacher}</span>
                    </div>

                    {course.classes.length > 0 && (
                      <div className="edt-classes">
                        <i className="ti ti-users" />
                        <span>
                          {course.classes.map((c: any) => c.label).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            }),
        ),
      )}
    </div>
  );

  return (
    <>
      {/* ── Navigation mobile : sélecteur de jour ── */}
      <div className="edt-mobile-day-nav">
        {days.map((day, idx) => (
          <button
            key={day.id}
            className={`edt-day-btn ${activeDayIndex === idx ? "active" : ""}`}
            onClick={() => setActiveDayIndex(idx)}
          >
            {day.name.slice(0, 3)}
          </button>
        ))}
      </div>

      {/* ── Vue mobile : un seul jour ── */}
      <div className="edt-grid-wrapper edt-mobile-view" ref={scrollRef}>
        {days[activeDayIndex] && renderGrid([days[activeDayIndex]])}
      </div>

      {/* ── Vue desktop / tablette : tous les jours avec scroll horizontal ── */}
      <div className="edt-grid-wrapper edt-desktop-view" ref={scrollRef}>
        {renderGrid(visibleDays)}
      </div>

      {/* ── CSS ── */}
      <style>{`
        /* ===== Variables ===== */
        .edt-grid-wrapper {
          --edt-time-col-width: 72px;
          --edt-day-min-width: 210px;
          --edt-border: 1px solid #e8eaf0;
          --edt-radius: 8px;
          --edt-bg-header: #ffffff;
          --edt-bg-cell: #ffffff;
          --edt-bg-time: #f8f9fc;
          --edt-text-muted: #9098b1;
          --edt-shadow-card: 0 1px 4px rgba(0,0,0,0.07);
        }

        /* ===== Navigation mobile (day tabs) ===== */
        .edt-mobile-day-nav {
          display: none;
          gap: 6px;
          flex-wrap: wrap;
          padding: 8px 4px 12px;
          border-bottom: var(--edt-border);
          margin-bottom: 4px;
        }

        .edt-day-btn {
          flex: 1;
          min-width: 48px;
          padding: 6px 4px;
          border: 1px solid #d1d5e0;
          border-radius: 6px;
          background: #f5f6fa;
          font-size: 12px;
          font-weight: 500;
          color: #4a5068;
          cursor: pointer;
          transition: all 0.15s ease;
          text-align: center;
        }

        .edt-day-btn.active {
          background: #2c3e8c;
          color: #fff;
          border-color: #2c3e8c;
        }

        /* ===== Wrapper ===== */
        .edt-grid-wrapper {
          overflow: auto;
          position: relative;
          -webkit-overflow-scrolling: touch;
        }

        /* Mobile view only shown on small screens */
        .edt-mobile-view { display: none; }
        .edt-desktop-view { display: block; }

        /* ===== Grid ===== */
        .edt-grid {
          display: grid;
          width: 100%;
          position: relative;
          gap: 4px;
          padding-bottom: 8px;
        }

        /* ===== Entêtes des jours ===== */
        .edt-day-header {
          position: sticky;
          top: 0;
          z-index: 30;
          background: var(--edt-bg-header);
          font-weight: 600;
          font-size: 13px;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 2px solid #e8eaf0;
          color: #3a4166;
          letter-spacing: 0.02em;
          min-height: 50px;
        }

        .edt-day-header-empty {
          background: var(--edt-bg-time);
          position: sticky;
          left: 0;
          z-index: 40;
          border-bottom: 2px solid #e8eaf0;
        }

        /* ===== Colonne des heures ===== */
        .edt-time-slot {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          padding: 6px 4px;
          background: var(--edt-bg-time);
          position: sticky;
          left: 0;
          z-index: 10;
          border-right: var(--edt-border);
        }

        .edt-time-label {
          font-size: 11px;
          font-weight: 500;
          color: var(--edt-text-muted);
          white-space: nowrap;
        }

        .edt-time-end {
          opacity: 0.6;
        }

        /* ===== Cellules vides ===== */
        .edt-grid-cell {
          background: transparent;
        }

        .edt-empty-course-card {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          font-size: 12px;
          color: #b0b8d0;
          border: 1.5px dashed #d5d9e8;
          border-radius: var(--edt-radius);
          background: var(--edt-bg-cell);
        }

        /* ===== Carte cours ===== */
        .edt-course-card {
          border-radius: var(--edt-radius);
          padding: 10px 10px;
          display: flex;
          height: 100%;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          overflow: hidden;
          font-size: 13px;
          box-shadow: var(--edt-shadow-card);
          cursor: pointer;
          transition: box-shadow 0.2s ease, transform 0.15s ease;
        }

        .edt-course-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.13);
          transform: translateY(-1px);
        }

        .edt-course-time {
          font-size: 11.5px;
          display: flex;
          align-items: center;
          gap: 5px;
          color: #5a6280;
          font-weight: 500;
          align-self: flex-start;
        }

        .edt-course-body {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
        }

        .edt-course-title {
          font-weight: 700;
          font-size: 15px;
          text-align: center;
          line-height: 1.3em;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
        }

        .edt-course-footer {
          margin-top: auto;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
        }

        .edt-course-room {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11.5px;
          color: #6b7494;
        }

        .edt-teacher {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #4a5068;
          width: 100%;
          justify-content: center;
        }

        .edt-teacher-name {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 120px;
        }

        .edt-classes {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #6b7494;
          text-align: center;
        }

        /* ===== Tablette (≤ 992px) ===== */
        @media (max-width: 991.98px) {
          .edt-grid-wrapper {
            --edt-time-col-width: 64px;
            --edt-day-min-width: 175px;
          }

          .edt-course-card {
            padding: 8px 6px;
          }

          .edt-course-title {
            font-size: 13px;
          }

          .edt-teacher-name {
            max-width: 90px;
          }
        }

        /* ===== Mobile (≤ 575px) : vue journée unique ===== */
        @media (max-width: 575.98px) {
          .edt-mobile-day-nav {
            display: flex;
          }

          .edt-mobile-view {
            display: block;
          }

          .edt-desktop-view {
            display: none;
          }

          .edt-mobile-view .edt-grid-wrapper,
          .edt-mobile-view {
            --edt-time-col-width: 58px;
            --edt-day-min-width: 0px;
          }

          /* Sur mobile, la colonne du jour prend tout l'espace restant */
          .edt-mobile-view .edt-grid {
            grid-template-columns: 58px 1fr !important;
          }

          .edt-course-title {
            font-size: 14px;
          }

          .edt-time-label {
            font-size: 10px;
          }

          .edt-course-card {
            padding: 8px 6px;
            gap: 4px;
          }

          .edt-course-footer {
            gap: 4px;
          }

          /* Masquer les avatars sur très petit écran */
          .edt-teacher .avvvatars {
            display: none;
          }

          .edt-teacher-name {
            max-width: 100%;
          }
        }

        /* ===== Très petits écrans (≤ 380px) ===== */
        @media (max-width: 380px) {
          .edt-day-btn {
            font-size: 11px;
            padding: 5px 3px;
          }

          .edt-course-title {
            font-size: 13px;
            -webkit-line-clamp: 3;
          }

          .edt-course-footer {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default StudentEDTGrid;
